import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  FileUp,
  Loader2,
  Search,
  Send,
  Users,
  Building2,
  CheckCircle2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { farmGet, farmPost, STATO_RICHIESTA_LABELS } from "./shared";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";

const STATO_PREVIEW = {
  ok: { label: "Pronto", className: "border-emerald-200 text-emerald-700" },
  conferma_cf: { label: "Conferma ospite", className: "border-amber-200 text-amber-700" },
  associazione_manuale: { label: "Associa ospite", className: "border-amber-200 text-amber-700" },
  ospite_non_trovato: { label: "Ospite non trovato", className: "border-rose-200 text-rose-700" },
  duplicato: { label: "Duplicato", className: "border-slate-300 text-slate-600" },
  illeggibile: { label: "Illeggibile / AI incompleta", className: "border-rose-200 text-rose-700" },
  errore: { label: "Errore", className: "border-rose-200 text-rose-700" },
  creato: { label: "Caricato", className: "border-emerald-200 text-emerald-700" },
};

export default function RichiesteCarepy() {
  const { token } = useFarmaciaAuth();
  const [searchParams] = useSearchParams();
  const [ospiti, setOspiti] = useState([]);
  const [richieste, setRichieste] = useState([]);
  const [strutture, setStrutture] = useState([]);
  const [search, setSearch] = useState("");
  const [orgFilter, setOrgFilter] = useState(() => searchParams.get("organization_id") || "");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [sending, setSending] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [medici, setMedici] = useState([]);
  const [doctorId, setDoctorId] = useState("");
  const [preselectDone, setPreselectDone] = useState(false);
  const [queue, setQueue] = useState([]);
  const [importazioni, setImportazioni] = useState([]);

  useEffect(() => {
    const fromUrl = searchParams.get("organization_id") || "";
    if (fromUrl) setOrgFilter(fromUrl);
  }, [searchParams]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [o, r, s, hist] = await Promise.all([
        farmGet("/farmacia/ricette/ospiti", token, orgFilter ? { organization_id: orgFilter } : undefined),
        farmGet("/farmacia/ricette/richieste", token, {
          ambito: "struttura",
          ...(orgFilter ? { organization_id: orgFilter } : {}),
        }),
        farmGet("/farmacia/strutture", token, { lite: true }),
        farmGet("/farmacia/ricette/carepy/importazioni", token, { limit: 15 }).catch(() => ({ items: [] })),
      ]);
      setOspiti(o.items || []);
      setRichieste(r.items || []);
      setStrutture(s.items || []);
      setImportazioni(hist.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [token, orgFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openOspite = useCallback(async (o) => {
    setSelected(o);
    setNote("");
    setDoctorId(o.doctor_id || "");
    setMedici([]);
    if (o.invio_medico_abilitato && o.organization_id) {
      try {
        const res = await farmGet("/farmacia/ricette/medici", token, {
          organization_id: o.organization_id,
        });
        setMedici(res.items || []);
      } catch {
        /* consenso mancante o errore */
      }
    }
  }, [token]);

  useEffect(() => {
    if (preselectDone || loading || !ospiti.length) return;
    const fid = searchParams.get("fascicolo_id");
    if (!fid) {
      setPreselectDone(true);
      return;
    }
    const match = ospiti.find((o) => o.fascicolo_id === fid);
    if (match) openOspite(match);
    setPreselectDone(true);
  }, [ospiti, loading, searchParams, preselectDone, openOspite]);

  const filteredOspiti = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ospiti;
    return ospiti.filter(
      (o) =>
        `${o.cognome} ${o.nome}`.toLowerCase().includes(q) ||
        (o.codice_fiscale || "").toLowerCase().includes(q)
    );
  }, [ospiti, search]);

  const canInvioMedico = useMemo(() => {
    if (!selected?.invio_medico_abilitato) return false;
    if (doctorId) {
      const m = medici.find((x) => x.id === doctorId);
      return !m || !!m.has_email;
    }
    return !!(selected.medico_email || medici.some((m) => m.has_email));
  }, [selected, doctorId, medici]);

  const analyzeFiles = async (fileList) => {
    const files = [...(fileList || [])];
    if (!files.length) return;
    setAnalyzing(true);
    const results = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (orgFilter) fd.append("organization_id", orgFilter);
        if (selected?.fascicolo_id) fd.append("fascicolo_id", selected.fascicolo_id);
        const res = await farmPost("/farmacia/ricette/carepy/analyze", token, fd, true);
        results.push({
          localId: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
          fileName: file.name,
          ...res,
          chosenFascicoloId: res.suggested?.fascicolo_id || selected?.fascicolo_id || "",
          chosenOrgId: res.suggested?.organization_id || selected?.organization_id || orgFilter || "",
        });
      } catch (e) {
        results.push({
          localId: `${file.name}-err-${Date.now()}`,
          fileName: file.name,
          stato: "errore",
          error: e?.response?.data?.detail || "Analisi fallita",
        });
      }
    }
    setQueue((prev) => [...results, ...prev]);
    setAnalyzing(false);
    toast.message(`${results.length} file analizzati`);
  };

  const confirmItem = async (item, { forceDuplicate = false } = {}) => {
    if (!item.preview_id) return;
    const fascicoloId = item.chosenFascicoloId;
    const orgId = item.chosenOrgId;
    if (!fascicoloId || !orgId) {
      toast.error("Seleziona l'ospite da associare");
      return;
    }
    setConfirming(item.localId);
    try {
      const fd = new FormData();
      fd.append("preview_id", item.preview_id);
      fd.append("fascicolo_id", fascicoloId);
      fd.append("organization_id", orgId);
      if (note) fd.append("note", note);
      if (forceDuplicate) fd.append("force_duplicate", "true");
      const doc = await farmPost("/farmacia/ricette/carepy/confirm", token, fd, true);
      toast.success(
        doc?.ciclo_carepy_stato === "carepy_ricevuto"
          ? `Carepy caricato: attesa del ciclo soddisfatta`
          : `Carepy caricato: ${item.fileName}`
      );
      setQueue((prev) =>
        prev.map((q) =>
          q.localId === item.localId ? { ...q, stato: "creato", richiesta_id: doc.id } : q
        )
      );
      await load();
      if (doc?.id) setChecked((prev) => new Set([...prev, doc.id]));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 || detail?.duplicato) {
        toast.error(detail?.message || "Documento duplicato");
        setQueue((prev) =>
          prev.map((q) =>
            q.localId === item.localId
              ? {
                  ...q,
                  stato: "duplicato",
                  duplicato_richiesta_id: detail?.richiesta_id || q.duplicato_richiesta_id,
                }
              : q
          )
        );
      } else {
        toast.error(typeof detail === "string" ? detail : detail?.message || "Conferma fallita");
      }
    } finally {
      setConfirming(null);
    }
  };

  const confirmAllReady = async () => {
    const ready = queue.filter((q) =>
      ["ok", "illeggibile", "conferma_cf", "associazione_manuale"].includes(q.stato) &&
      q.chosenFascicoloId &&
      q.preview_id
    );
    for (const item of ready) {
      // eslint-disable-next-line no-await-in-loop
      await confirmItem(item);
    }
  };

  const inviaMedico = async (reqId) => {
    if (!canInvioMedico) {
      toast.error(
        selected?.invio_medico_abilitato
          ? "Nessuna email medico disponibile"
          : "La struttura non ha abilitato l'inoltro ai medici"
      );
      return;
    }
    setSending(true);
    try {
      await farmPost(`/farmacia/ricette/richieste/${reqId}/invia-medico`, token, {
        doctor_id: doctorId || undefined,
      });
      toast.success("Richiesta inviata al medico");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio fallito");
    } finally {
      setSending(false);
    }
  };

  const inviaBulk = async () => {
    const ids = [...checked];
    if (!ids.length) return;
    if (!canInvioMedico) {
      toast.error(
        selected?.invio_medico_abilitato
          ? "Nessuna email medico disponibile"
          : "Seleziona un ospite di una struttura con inoltro abilitato e email medico"
      );
      return;
    }
    setSending(true);
    try {
      await farmPost("/farmacia/ricette/richieste/invia-medico-bulk", token, {
        richiesta_ids: ids,
        doctor_id: doctorId || undefined,
      });
      toast.success(`Inviate ${ids.length} richieste`);
      setChecked(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio bulk fallito");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FarmaciaPageChrome>
        {checked.size > 0 ? (
          <Button
            onClick={inviaBulk}
            disabled={sending || !canInvioMedico}
            size="sm"
            className="rounded-xl bg-[#4CAF50] hover:bg-[#43A047]"
            title={!canInvioMedico ? "Inoltro non disponibile" : undefined}
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Invia al medico ({checked.size})
          </Button>
        ) : null}
      </FarmaciaPageChrome>

      <div className="flex flex-col gap-2 sm:flex-row">
        <select
          className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm"
          value={orgFilter}
          onChange={(e) => setOrgFilter(e.target.value)}
        >
          <option value="">Tutte le strutture</option>
          {strutture.map((s) => (
            <option key={s.organization_id || s.id} value={s.organization_id || s.id}>
              {s.nome || s.name}
            </option>
          ))}
        </select>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-11 rounded-xl pl-9"
            placeholder="Cerca ospite o codice fiscale…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="rounded-xl shadow-sm">
          <CardHeader className="border-b px-4 py-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-emerald-600" />
              Ospiti aderenti
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[420px] space-y-1 overflow-y-auto p-2">
            {filteredOspiti.length === 0 ? (
              <p className="p-6 text-center text-sm text-slate-400">Nessun ospite</p>
            ) : (
              filteredOspiti.map((o) => (
                <button
                  key={o.fascicolo_id}
                  type="button"
                  onClick={() => openOspite(o)}
                  className={`w-full rounded-xl px-3 py-2.5 text-left text-sm transition ${
                    selected?.fascicolo_id === o.fascicolo_id
                      ? "bg-emerald-50/70"
                      : "border-transparent hover:bg-slate-50"
                  }`}
                >
                  <div className="font-medium text-slate-900">
                    {o.cognome} {o.nome}
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {o.struttura_nome || "—"}
                    </span>
                    <span>·</span>
                    <span className="font-mono">{o.codice_fiscale || "CF —"}</span>
                    {o.invio_medico_abilitato && (
                      <span className="text-emerald-600">· Inoltro OK</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="rounded-xl shadow-sm">
          <CardHeader className="border-b px-4 py-3">
            <CardTitle className="text-base">Import Carepy</CardTitle>
            <p className="text-xs text-slate-500">
              {selected
                ? `Ospite preferito: ${selected.cognome} ${selected.nome}`
                : "Puoi caricare senza selezione: l’associazione usa il codice fiscale del PDF"}
            </p>
          </CardHeader>
          <CardContent className="space-y-4 p-4 text-sm">
            <div>
              <label className="text-xs font-medium text-slate-600">PDF Carepy (singolo o multipli)</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                className="mt-1 block w-full text-sm"
                disabled={analyzing}
                onChange={(e) => {
                  analyzeFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Note (opzionale, per le conferme)</label>
              <textarea
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            {selected?.invio_medico_abilitato && medici.length > 0 && (
              <div>
                <label className="text-xs font-medium text-slate-600">Medico destinatario</label>
                <select
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  value={doctorId}
                  onChange={(e) => setDoctorId(e.target.value)}
                >
                  <option value="">Medico curante / predefinito</option>
                  {medici.map((m) => (
                    <option key={m.id} value={m.id} disabled={!m.has_email}>
                      {m.name} {m.has_email ? "" : "(email mancante)"}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {selected && !selected.invio_medico_abilitato && (
              <p className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                La struttura non ha abilitato l&apos;inoltro ai medici: puoi caricare i Carepy, ma non
                inviarli dal portale.
              </p>
            )}
            {analyzing && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                Analisi in corso…
              </div>
            )}
            {queue.some((q) =>
              ["ok", "illeggibile", "conferma_cf", "associazione_manuale"].includes(q.stato)
            ) && (
              <Button
                onClick={confirmAllReady}
                disabled={!!confirming}
                className="w-full rounded-xl bg-[#4CAF50] hover:bg-[#43A047]"
              >
                <FileUp className="mr-2 h-4 w-4" />
                Conferma tutti i file pronti
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {queue.length > 0 && (
        <Card className="rounded-xl shadow-sm">
          <CardHeader className="border-b px-4 py-3">
            <CardTitle className="text-sm">Coda import</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 p-4">
            {queue.map((item) => {
              const meta = STATO_PREVIEW[item.stato] || STATO_PREVIEW.errore;
              return (
                <div
                  key={item.localId}
                  className="rounded-xl border border-slate-200/80 bg-white p-3 text-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-slate-900">{item.fileName}</p>
                      {item.meta?.codice_fiscale && (
                        <p className="mt-0.5 font-mono text-xs text-slate-500">
                          CF rilevato: {item.meta.codice_fiscale}
                          {item.meta.periodo ? ` · Periodo ${item.meta.periodo}` : ""}
                          {item.meta.data_elaborazione
                            ? ` · Elaborazione ${item.meta.data_elaborazione}`
                            : ""}
                          {item.meta.carepy_data_attesa
                            ? ` · Prossimo Carepy entro ${item.meta.carepy_data_attesa}`
                            : ""}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] ${meta.className}`}>
                        {meta.label}
                      </Badge>
                      <button
                        type="button"
                        className="text-slate-400 hover:text-slate-600"
                        onClick={() =>
                          setQueue((prev) => prev.filter((q) => q.localId !== item.localId))
                        }
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {(item.farmaci || []).length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-slate-600">
                      {item.farmaci.slice(0, 6).map((f, idx) => (
                        <li key={idx}>
                          {f.farmaco}
                          {f.unita_residue != null
                            ? ` · Unità residue rilevate da Carepy: ${f.unita_residue}`
                            : ""}
                        </li>
                      ))}
                      {item.farmaci.length > 6 ? (
                        <li className="text-slate-400">+{item.farmaci.length - 6} altri</li>
                      ) : null}
                    </ul>
                  )}

                  {item.illeggibile && (
                    <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      File poco leggibile o senza farmaci estratti: puoi comunque associare e
                      caricare il PDF originale.
                    </p>
                  )}

                  {item.stato !== "creato" && item.stato !== "errore" && (
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
                      <div className="flex-1">
                        <label className="mb-1 block text-[11px] text-slate-500">Ospite associato</label>
                        <select
                          className="h-9 w-full rounded-lg border border-slate-200 px-2 text-sm"
                          value={item.chosenFascicoloId || ""}
                          onChange={(e) => {
                            const fid = e.target.value;
                            const o = ospiti.find((x) => x.fascicolo_id === fid);
                            setQueue((prev) =>
                              prev.map((q) =>
                                q.localId === item.localId
                                  ? {
                                      ...q,
                                      chosenFascicoloId: fid,
                                      chosenOrgId: o?.organization_id || q.chosenOrgId,
                                      stato:
                                        q.stato === "duplicato"
                                          ? q.stato
                                          : fid
                                            ? "ok"
                                            : q.stato,
                                    }
                                  : q
                              )
                            );
                          }}
                        >
                          <option value="">— seleziona —</option>
                          {(item.matches || []).map((m) => (
                            <option key={`m-${m.fascicolo_id}`} value={m.fascicolo_id}>
                              {m.cognome} {m.nome} (CF match)
                            </option>
                          ))}
                          {ospiti
                            .filter(
                              (o) => !(item.matches || []).some((m) => m.fascicolo_id === o.fascicolo_id)
                            )
                            .map((o) => (
                              <option key={o.fascicolo_id} value={o.fascicolo_id}>
                                {o.cognome} {o.nome} · {o.struttura_nome || ""}
                              </option>
                            ))}
                        </select>
                      </div>
                      {item.stato === "duplicato" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-lg"
                          disabled={confirming === item.localId || !item.chosenFascicoloId}
                          onClick={() => confirmItem(item, { forceDuplicate: true })}
                        >
                          {confirming === item.localId ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            "Carica comunque"
                          )}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          className="rounded-lg bg-[#4CAF50] hover:bg-[#43A047]"
                          disabled={confirming === item.localId || !item.chosenFascicoloId}
                          onClick={() => confirmItem(item)}
                        >
                          {confirming === item.localId ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            "Conferma e carica"
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card className="rounded-xl shadow-sm">
        <CardHeader className="border-b px-4 py-3">
          <CardTitle className="text-sm">Richieste recenti</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50/60 text-left text-xs uppercase text-slate-500">
                <th className="px-3 py-2 w-8" />
                <th className="px-3 py-2">Ospite</th>
                <th className="px-3 py-2">Data</th>
                <th className="px-3 py-2">Stato</th>
                <th className="px-3 py-2">Farmaci</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {richieste.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    Nessuna richiesta
                  </td>
                </tr>
              ) : (
                richieste.map((r) => {
                  const open = !["inviata_medico", "ricette_ricevute", "chiusa"].includes(r.stato);
                  const ospiteReq = ospiti.find((o) => o.fascicolo_id === r.fascicolo_id);
                  const invioOk = !!(
                    ospiteReq?.invio_medico_abilitato &&
                    (ospiteReq.medico_email ||
                      (selected?.fascicolo_id === r.fascicolo_id && canInvioMedico))
                  );
                  return (
                    <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={checked.has(r.id)}
                          disabled={!open}
                          onChange={() => {
                            setChecked((prev) => {
                              const n = new Set(prev);
                              if (n.has(r.id)) n.delete(r.id);
                              else n.add(r.id);
                              return n;
                            });
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium">
                        {r.ospite_cognome} {r.ospite_nome}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {(r.data_richiesta || r.created_at || "").slice(0, 10)}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="rounded-full font-normal">
                          {STATO_RICHIESTA_LABELS[r.stato] || r.stato}
                        </Badge>
                        {r.origine === "portale_farmacia" && (
                          <span className="ml-1 text-[10px] text-emerald-600">portale</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {(r.farmaci_richiesti || []).length}
                      </td>
                      <td className="px-3 py-2">
                        {open && invioOk && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={sending}
                            onClick={() => inviaMedico(r.id)}
                            className="text-emerald-700"
                          >
                            <Send className="mr-1 h-3.5 w-3.5" />
                            Invia
                          </Button>
                        )}
                        {open && !invioOk && (
                          <span className="text-[11px] text-slate-400">Invio non disponibile</span>
                        )}
                        {r.stato === "inviata_medico" && (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {importazioni.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Storico importazioni Carepy</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {importazioni.map((h) => (
                <li
                  key={h.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2"
                >
                  <div>
                    <div className="font-medium text-slate-800">
                      {h.ospite_nome || h.codice_fiscale || "Ospite"}
                    </div>
                    <div className="text-xs text-slate-500">
                      {h.file_name || "PDF"}
                      {h.farmaci_count != null ? ` · ${h.farmaci_count} farmaci` : ""}
                      {h.created_by_nome ? ` · ${h.created_by_nome}` : ""}
                    </div>
                  </div>
                  <span className="text-xs text-slate-400">
                    {h.created_at ? String(h.created_at).replace("T", " ").slice(0, 16) : "—"}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
